/*
 * lsbh-delivery-pickup.js  (T18)
 * Reusable delivery + pickup date-picker pair for BOTH the product add-to-cart
 * form and the cart page line items. Depends on flatpickr (already loaded via
 * assets/lsbh-flatpickr.min.js).
 *
 * Markup contract — a container with:
 *   [data-lsbh-dp]                 marks a delivery+pickup pair
 *   data-context="product|cart"    where it lives
 *   data-hire-days="7"             included hire period (fallback 7)
 *   data-max-hire-days="28"        optional max hire cap (fallback 28)
 *   data-line-key / data-variant-id / data-existing-props  (cart only)
 * containing:
 *   input[data-lsbh-dp-delivery]   delivery input (name=properties[Preferred delivery date] on product)
 *   input[data-lsbh-dp-pickup]     pickup input   (name=properties[Preferred pickup date] on product)
 * On the cart, each input may carry data-initial with the stored value.
 *
 * Rules (reuse the existing Sydney/Sat delivery rule):
 *   Delivery: min = (Sydney today is Saturday ? next Monday : tomorrow); Sundays disabled.
 *   Pickup:   default = delivery + hire days; min = delivery + 1; max = delivery + maxHire; Sundays disabled.
 *             When delivery changes, re-default pickup and move its min/max.
 * Cart edits persist to the line item via POST /cart/change.js, merging (never
 * dropping) the item's other existing properties.
 *
 * Extended hire (over 7 days) — NOT a block. Every date the picker already offered stays
 * selectable and nothing is ever rejected at add-to-cart. Instead:
 *   - The moment a chosen range exceeds INCLUDED_HIRE_DAYS, a modal explains the surcharge.
 *     "Continue booking" keeps the selection; "Change dates" reopens the pickup calendar.
 *   - Any over-7 range attaches the line item property `Extended hire` in ALL cases, whether
 *     or not the modal was ever seen — the modal is the explanation, the property is the record.
 *   - window.LSBH_hireGuard(form) is the add-to-cart backstop: a range that arrives via URL
 *     param / back-button / prefill never passes through the pickers' onChange, so it reaches
 *     submit unacknowledged. The guard prompts there and attaches the property.
 *   - 7 days or fewer: no modal, no property, byte-for-byte the old behaviour.
 */
(function () {
  'use strict';

  var FMT = 'l, j M Y';

  // Hire longer than this is allowed, but carries a surcharge the office confirms after booking.
  var INCLUDED_HIRE_DAYS = 7;
  var EXT_PROP_NAME = 'Extended hire';

  function sydneyToday() {
    var WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    var parts = new Intl.DateTimeFormat('en-AU', {
      timeZone: 'Australia/Sydney', weekday: 'long', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false
    }).formatToParts(new Date());
    var o = {};
    for (var i = 0; i < parts.length; i++) { o[parts[i].type] = parts[i].value; }
    var hh = parseInt(o.hour, 10); if (hh === 24) { hh = 0; } // en-AU can emit "24" at midnight
    return { dow: WEEK.indexOf(o.weekday), year: parseInt(o.year, 10), month: parseInt(o.month, 10),
             day: parseInt(o.day, 10), hour: hh, minute: parseInt(o.minute, 10) };
  }

  // T7 rule: 8pm (20:00) Australia/Sydney cutoff — order before 8pm for next-day delivery;
  // at/after 8pm the promise (and this min date) roll one more day. Sundays are blocked
  // (which also gives the Sat->Mon behaviour). The countdown line uses the SAME function so
  // the promised date always equals the picker's minimum selectable date.
  function deliveryMinDate() {
    var t = sydneyToday();
    if (t.dow < 0) { return new Date(); }
    var offset = (t.hour >= 20) ? 2 : 1;
    var d = new Date(t.year, t.month - 1, t.day + offset);
    while (d.getDay() === 0) { d = addDays(d, 1); }
    return d;
  }

  function addDays(d, n) { return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n); }
  function isSunday(d) { return d.getDay() === 0; }
  function nextNonSunday(d) { var x = new Date(d.getTime()); while (isSunday(x)) { x = addDays(x, 1); } return x; }

  // Whole-day arithmetic via UTC day index: immune to DST shifts, which a raw ms/864e5
  // divide is not (an AEDT->AEST boundary inside the window would otherwise round wrong).
  function dayNum(d) { return Math.floor(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 864e5); }
  function daysBetween(a, b) { return dayNum(b) - dayNum(a); }

  function parseVal(s) {
    if (!s) { return null; }
    try { return flatpickr.parseDate(s, FMT) || null; } catch (e) { return null; }
  }

  /* ===== Extended-hire modal (shared by product + cart, built once) ===== */

  var MODAL_STYLE_ID = 'lsbh-hire-modal-style';
  var modalEl = null;

  function ensureModalStyle() {
    if (document.getElementById(MODAL_STYLE_ID)) { return; }
    var s = document.createElement('style');
    s.id = MODAL_STYLE_ID;
    s.textContent =
      '.lsbh-hire-modal{position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;' +
      'justify-content:center;padding:16px;font-family:"Nunito",sans-serif}' +
      '.lsbh-hire-modal[hidden]{display:none}' +
      '.lsbh-hire-modal__overlay{position:absolute;inset:0;background:rgba(11,44,74,.55)}' +
      '.lsbh-hire-modal__dialog{position:relative;width:100%;max-width:420px;background:#fff;' +
      'border-radius:16px;padding:22px 20px 18px;box-shadow:0 24px 60px rgba(11,44,74,.35);' +
      'max-height:calc(100vh - 32px);overflow-y:auto}' +
      '.lsbh-hire-modal__heading{margin:0 0 10px;font-size:19px;line-height:1.25;font-weight:900;' +
      'color:#0b2c4a;font-family:"Nunito",sans-serif}' +
      '.lsbh-hire-modal__body{margin:0 0 18px;font-size:14px;line-height:1.5;font-weight:600;color:#5a6b7b}' +
      '.lsbh-hire-modal__actions{display:flex;flex-direction:column;gap:9px}' +
      '.lsbh-hire-modal__btn{width:100%;box-sizing:border-box;padding:13px 16px;border-radius:12px;' +
      'font-family:"Nunito",sans-serif;font-size:15px;font-weight:800;cursor:pointer;border:1px solid transparent}' +
      '.lsbh-hire-modal__btn--primary{background:#358f51;color:#fff;border-color:#358f51}' +
      '.lsbh-hire-modal__btn--primary:hover{background:#2d7a45}' +
      '.lsbh-hire-modal__btn--secondary{background:#fff;color:#0b2c4a;border-color:#cdd8e3}' +
      '.lsbh-hire-modal__btn--secondary:hover{border-color:#0b2c4a}' +
      '.lsbh-hire-modal__btn:focus-visible{outline:2px solid #358f51;outline-offset:2px}' +
      '@media (min-width:480px){.lsbh-hire-modal__actions{flex-direction:row-reverse}' +
      '.lsbh-hire-modal__btn{width:auto;flex:1 1 0}}';
    (document.head || document.documentElement).appendChild(s);
  }

  // Verbatim copy — heading, body and both button labels are fixed.
  function ensureModal() {
    if (modalEl) { return modalEl; }
    ensureModalStyle();
    var wrap = document.createElement('div');
    wrap.className = 'lsbh-hire-modal';
    wrap.setAttribute('data-lsbh-hire-modal', '');
    wrap.hidden = true;
    wrap.innerHTML =
      '<div class="lsbh-hire-modal__overlay" data-lsbh-hire-overlay></div>' +
      '<div class="lsbh-hire-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="lsbh-hire-modal-h">' +
        '<h2 class="lsbh-hire-modal__heading" id="lsbh-hire-modal-h">Hiring longer than 7 days?</h2>' +
        '<p class="lsbh-hire-modal__body">7 days of hire is always included in your price. Hires longer than 7 days incur a surcharge, which varies by bin size — our office will confirm the exact amount with you after you book.</p>' +
        '<div class="lsbh-hire-modal__actions">' +
          '<button type="button" class="lsbh-hire-modal__btn lsbh-hire-modal__btn--primary" data-lsbh-hire-continue>Continue booking</button>' +
          '<button type="button" class="lsbh-hire-modal__btn lsbh-hire-modal__btn--secondary" data-lsbh-hire-change>Change dates</button>' +
        '</div>' +
      '</div>';
    document.body.appendChild(wrap);
    modalEl = wrap;
    return wrap;
  }

  var modalHandlers = null;

  function closeModal() {
    if (!modalEl) { return; }
    modalEl.hidden = true;
    modalHandlers = null;
    document.removeEventListener('keydown', onModalKey);
  }

  function onModalKey(e) {
    // Escape is a non-decision: leave the selection as-is and let the add-to-cart guard
    // re-prompt, rather than silently counting it as acknowledgement.
    if (e.key === 'Escape') { closeModal(); }
  }

  // onContinue: selection stands. onChangeDates: hand focus back to the picker.
  function openModal(onContinue, onChangeDates) {
    var m = ensureModal();
    modalHandlers = { onContinue: onContinue, onChangeDates: onChangeDates };
    if (!m.__lsbhBound) {
      m.querySelector('[data-lsbh-hire-continue]').addEventListener('click', function () {
        var h = modalHandlers; closeModal(); if (h && h.onContinue) { h.onContinue(); }
      });
      m.querySelector('[data-lsbh-hire-change]').addEventListener('click', function () {
        var h = modalHandlers; closeModal(); if (h && h.onChangeDates) { h.onChangeDates(); }
      });
      m.querySelector('[data-lsbh-hire-overlay]').addEventListener('click', function () { closeModal(); });
      m.__lsbhBound = true;
    }
    m.hidden = false;
    document.addEventListener('keydown', onModalKey);
    try { m.querySelector('[data-lsbh-hire-continue]').focus({ preventScroll: true }); } catch (e) {}
  }

  function initPair(container) {
    if (container.getAttribute('data-lsbh-dp-init') === '1') { return; }
    if (typeof flatpickr === 'undefined') { return; }

    // Load & Go single-date mode: pickup only, no delivery input, no pair re-default.
    // Used on the product page for Waste Removal services (data-single="pickup").
    // The value submits via the input's name=properties[Preferred pickup date]; there is
    // no cart persist() path in single mode, so the CART renders service lines read-only
    // (no [data-lsbh-dp]) rather than an editable-but-non-saving picker.
    var single = container.getAttribute('data-single') === 'pickup';
    if (single) {
      var pickOnly = container.querySelector('[data-lsbh-dp-pickup]');
      if (!pickOnly) { return; }
      container.setAttribute('data-lsbh-dp-init', '1');
      var fpOne = flatpickr(pickOnly, {
        minDate: deliveryMinDate(),                     // same next-day / Sat→Mon rule
        dateFormat: FMT,
        disable: [function (d) { return isSunday(d); }] // Sundays blocked
      });
      if (fpOne.calendarContainer) { fpOne.calendarContainer.classList.add('lsbh-fp'); }
      var seed = pickOnly.getAttribute('data-initial');
      if (seed) { fpOne.setDate(seed, false); }
      if (!fpOne.selectedDates[0]) { fpOne.setDate(deliveryMinDate(), false); }
      return;                                            // skip pair logic entirely
    }

    var delInput = container.querySelector('[data-lsbh-dp-delivery]');
    var pickInput = container.querySelector('[data-lsbh-dp-pickup]');
    if (!delInput || !pickInput) { return; }
    container.setAttribute('data-lsbh-dp-init', '1');

    var hireDays = parseInt(container.getAttribute('data-hire-days'), 10);
    if (!(hireDays > 0)) { hireDays = 7; }
    var maxHire = parseInt(container.getAttribute('data-max-hire-days'), 10);
    if (!(maxHire > 0)) { maxHire = 365; }  // OWNER 2026-07-18: any date (Sundays disabled); 1-year practical bound
    if (maxHire < hireDays) { maxHire = hireDays; }

    var context = container.getAttribute('data-context') || 'product';
    var lineKey = container.getAttribute('data-line-key') || '';
    var variantId = container.getAttribute('data-variant-id') || '';

    var minDel = deliveryMinDate();
    var existingDel = delInput.getAttribute('data-initial') || '';
    var existingPick = pickInput.getAttribute('data-initial') || '';

    var suppress = false; // guard programmatic pickup setDate from firing onChange/persist

    /* --- extended hire (over INCLUDED_HIRE_DAYS) --- */

    // Acknowledgement is keyed to the exact date pair, so changing the dates to a NEW
    // over-7 range re-prompts rather than riding on the previous "Continue booking".
    var ackKey = null;
    function selectionKey() { return (delInput.value || '') + '|' + (pickInput.value || ''); }

    // Product form only: the cart writes the same property through persist() instead.
    var extInput = null;
    function ensureExtInput() {
      if (extInput || context === 'cart') { return extInput; }
      extInput = container.querySelector('[data-lsbh-ext-hire]');
      if (!extInput) {
        extInput = document.createElement('input');
        extInput.type = 'hidden';
        extInput.name = 'properties[' + EXT_PROP_NAME + ']';
        extInput.setAttribute('data-lsbh-ext-hire', '');
        extInput.disabled = true; // disabled inputs are not submitted
        container.appendChild(extInput);
      }
      return extInput;
    }

    // Hire length from the raw input VALUES, so a prefilled/injected pair counts the same
    // as a clicked one. Returns 0 when either date is missing or unparseable.
    function hireDays_current() {
      var d = parseVal((delInput.value || '').trim());
      var p = parseVal((pickInput.value || '').trim());
      if (!d || !p) { return 0; }
      return daysBetween(d, p);
    }
    function isExtended() { return hireDays_current() > INCLUDED_HIRE_DAYS; }

    // Shows total AND overage, so the office can price the surcharge without doing the
    // subtraction. Renders on the order as:
    //   Extended hire: 21 days total (14 beyond included 7)
    function extPropValue() {
      var total = hireDays_current();
      return total + ' days total (' + (total - INCLUDED_HIRE_DAYS) + ' beyond included ' + INCLUDED_HIRE_DAYS + ')';
    }

    // Attaches on ANY over-7 range, acknowledged or not.
    function syncExtProp() {
      var el = ensureExtInput();
      if (!el) { return; }
      if (isExtended()) { el.disabled = false; el.value = extPropValue(); }
      else { el.disabled = true; el.value = ''; }
    }

    function promptExtended(onAfterContinue) {
      openModal(
        function () { ackKey = selectionKey(); syncExtProp(); if (onAfterContinue) { onAfterContinue(); } },
        function () { try { pickFp.open(); } catch (e) {} }
      );
    }

    // Fired on a real user pick: prompt immediately when the new range goes over.
    function handleSelectionChange() {
      syncExtProp();
      // OWNER 2026-07-18: surcharge popup removed; the [Extended hire] length property is still recorded by syncExtProp() above.
    }

    var pickFp = flatpickr(pickInput, {
      dateFormat: FMT,
      disable: [function (d) { return isSunday(d); }],
      onChange: function () {
        if (suppress) { return; }
        handleSelectionChange();
        persist();
      }
    });
    if (pickFp.calendarContainer) { pickFp.calendarContainer.classList.add('lsbh-fp'); }

    function applyPickupWindow(delDate, resetDefault) {
      var minPick = nextNonSunday(addDays(delDate, 1));
      var maxPick = addDays(delDate, maxHire);
      pickFp.set('minDate', minPick);
      pickFp.set('maxDate', maxPick);
      if (resetDefault) {
        var def = addDays(delDate, hireDays);
        if (isSunday(def)) { def = nextNonSunday(def); }
        if (def.getTime() < minPick.getTime()) { def = minPick; }
        if (def.getTime() > maxPick.getTime()) { def = maxPick; }
        suppress = true;
        pickFp.setDate(def, false);
        suppress = false;
      }
    }

    var delFp = flatpickr(delInput, {
      minDate: minDel,
      dateFormat: FMT,
      disable: [function (d) { return isSunday(d); }],
      onChange: function (sel) {
        var d = sel && sel[0];
        if (!d) { return; }
        applyPickupWindow(d, true); // re-default pickup on delivery change (back to an included-length hire)
        handleSelectionChange();
        persist();
      }
    });
    if (delFp.calendarContainer) { delFp.calendarContainer.classList.add('lsbh-fp'); }

    // Seed delivery: keep an existing stored value, else default to the min date.
    var startDel;
    if (existingDel) { delFp.setDate(existingDel, false); startDel = delFp.selectedDates[0]; }
    if (!startDel) { startDel = minDel; delFp.setDate(minDel, false); }

    // Seed pickup: keep an existing stored value if it fits the window, else default.
    applyPickupWindow(startDel, false);
    if (existingPick) {
      suppress = true; pickFp.setDate(existingPick, false); suppress = false;
    }
    if (!pickFp.selectedDates[0]) { applyPickupWindow(startDel, true); }

    // A seeded over-7 range (stored cart line, prefill, back-button) attaches the property
    // straight away but is deliberately NOT acknowledged and does NOT pop the modal on load —
    // an unrequested modal at page-load is hostile. The add-to-cart guard prompts instead.
    syncExtProp();

    // Expose for the add-to-cart guard, which runs from the product form's validator.
    container.__lsbhHire = {
      isExtended: isExtended,
      isAcknowledged: function () { return true; }, // OWNER 2026-07-18: popup removed — never hold the submit
      sync: syncExtProp,
      prompt: function (cb) { syncExtProp(); if (cb) { cb(); } } // record length, no modal
    };

    function currentVal(fp) { return fp.selectedDates[0] ? fp.formatDate(fp.selectedDates[0], FMT) : ''; }

    function persist() {
      if (context !== 'cart' || !lineKey) { return; }
      var delVal = currentVal(delFp);
      var pickVal = currentVal(pickFp);
      var existing = {};
      try { existing = JSON.parse(container.getAttribute('data-existing-props') || '{}') || {}; } catch (e) { existing = {}; }
      var props = {};
      for (var k in existing) { if (existing.hasOwnProperty(k)) { props[k] = existing[k]; } } // preserve ALL other props
      props['Preferred delivery date'] = delVal;
      props['Preferred pickup date'] = pickVal;
      // Keep the surcharge record in step with the dates on the line: editing a cart line
      // down to 7 days or fewer must clear it, not leave a stale surcharge flag on the order.
      if (isExtended()) { props[EXT_PROP_NAME] = extPropValue(); }
      else { delete props[EXT_PROP_NAME]; }

      container.setAttribute('data-lsbh-saving', '1');
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: lineKey, properties: props })
      }).then(function (r) { return r.json(); }).then(function (cart) {
        // Changing properties re-keys the line, so refresh key + stored props from the response.
        if (cart && cart.items) {
          for (var i = 0; i < cart.items.length; i++) {
            var it = cart.items[i];
            var p = it.properties || {};
            var idMatch = variantId ? (String(it.variant_id) === String(variantId)) : true;
            if (idMatch && p['Preferred delivery date'] === delVal && p['Preferred pickup date'] === pickVal) {
              lineKey = it.key;
              container.setAttribute('data-line-key', it.key);
              container.setAttribute('data-existing-props', JSON.stringify(p));
              break;
            }
          }
        }
        container.removeAttribute('data-lsbh-saving');
      }).catch(function () { container.removeAttribute('data-lsbh-saving'); });
    }
  }

  /* ===== OLT-001 — merged single range selector (product page) =====
   * ONE flatpickr in range mode. First click sets delivery; pickup auto-defaults to
   * delivery + included hire days and is adjustable (a second click) within the same
   * min/max limits. The two supplier-facing properties are written UNCHANGED to hidden
   * mirror inputs, same keys and same FMT, so order data / Step-3 validation / the review
   * summary / the booking smoke all read them exactly as before. Sundays disabled; the
   * extended-hire modal + property behave identically to the two-field version.
   */
  function initRange(container) {
    if (container.getAttribute('data-lsbh-dp-init') === '1') { return; }
    if (typeof flatpickr === 'undefined') { return; }

    var rangeInput = container.querySelector('[data-lsbh-dp-range]');
    var delHidden = container.querySelector('[data-lsbh-dp-delivery]');
    var pickHidden = container.querySelector('[data-lsbh-dp-pickup]');
    if (!rangeInput || !delHidden || !pickHidden) { return; }
    container.setAttribute('data-lsbh-dp-init', '1');

    var hireDays = parseInt(container.getAttribute('data-hire-days'), 10);
    if (!(hireDays > 0)) { hireDays = 7; }
    var maxHire = parseInt(container.getAttribute('data-max-hire-days'), 10);
    if (!(maxHire > 0)) { maxHire = 365; }  // OWNER 2026-07-18: any date (Sundays disabled); 1-year practical bound
    if (maxHire < hireDays) { maxHire = hireDays; }

    var suppress = false;
    var ackKey = null;
    function selectionKey() { return (delHidden.value || '') + '|' + (pickHidden.value || ''); }

    var extInput = null;
    function ensureExtInput() {
      if (extInput) { return extInput; }
      extInput = container.querySelector('[data-lsbh-ext-hire]');
      if (!extInput) {
        extInput = document.createElement('input');
        extInput.type = 'hidden';
        extInput.name = 'properties[' + EXT_PROP_NAME + ']';
        extInput.setAttribute('data-lsbh-ext-hire', '');
        extInput.disabled = true;
        container.appendChild(extInput);
      }
      return extInput;
    }
    function hireDays_current() {
      var d = parseVal((delHidden.value || '').trim());
      var p = parseVal((pickHidden.value || '').trim());
      if (!d || !p) { return 0; }
      return daysBetween(d, p);
    }
    function isExtended() { return hireDays_current() > INCLUDED_HIRE_DAYS; }
    function extPropValue() {
      var total = hireDays_current();
      return total + ' days total (' + (total - INCLUDED_HIRE_DAYS) + ' beyond included ' + INCLUDED_HIRE_DAYS + ')';
    }
    function syncExtProp() {
      var el = ensureExtInput();
      if (!el) { return; }
      if (isExtended()) { el.disabled = false; el.value = extPropValue(); }
      else { el.disabled = true; el.value = ''; }
    }
    function promptExtended(onAfterContinue) {
      openModal(
        function () { ackKey = selectionKey(); syncExtProp(); if (onAfterContinue) { onAfterContinue(); } },
        function () { try { fp.open(); } catch (e) {} }
      );
    }

    function fmt(d) { return fp.formatDate(d, FMT); }
    function defaultPickup(del) {
      var minPick = nextNonSunday(addDays(del, 1));
      var maxPick = addDays(del, maxHire);
      var def = addDays(del, hireDays);
      if (isSunday(def)) { def = nextNonSunday(def); }
      if (def.getTime() < minPick.getTime()) { def = minPick; }
      if (def.getTime() > maxPick.getTime()) { def = maxPick; }
      return def;
    }
    // Sundays are already unselectable in the picker, so a user-picked pickup is never a
    // Sunday; only the min (delivery+1) and max (delivery+maxHire) span need enforcing.
    function clampPickup(del, pick) {
      var minPick = nextNonSunday(addDays(del, 1));
      var maxPick = addDays(del, maxHire);
      if (pick.getTime() < minPick.getTime()) { return minPick; }
      if (pick.getTime() > maxPick.getTime()) { return maxPick; }
      return pick;
    }
    function commit(del, pick, userInitiated) {
      delHidden.value = fmt(del);
      pickHidden.value = fmt(pick);
      // Programmatic value sets don't fire input events; dispatch so the Step-3 review
      // summary (which listens on the form) refreshes.
      delHidden.dispatchEvent(new Event('input', { bubbles: true }));
      pickHidden.dispatchEvent(new Event('input', { bubbles: true }));
      syncExtProp();
      // OWNER 2026-07-18: surcharge popup removed. syncExtProp() above still records the [Extended hire] length; we no longer interrupt with a modal.
    }

    var fp = flatpickr(rangeInput, {
      mode: 'range',
      dateFormat: FMT,
      minDate: deliveryMinDate(),
      disable: [function (d) { return isSunday(d); }],
      onChange: function (sel) {
        if (suppress) { return; }
        if (!sel || sel.length === 0) { return; }
        if (sel.length === 1) {
          // First click = delivery. Keep the calendar OPEN so the shopper can pick a
          // pickup date anywhere up to the max hire window — they are NOT limited to one
          // week. Mirror the delivery now so validation still has it if they close without
          // choosing a pickup (onClose defaults it then).
          delHidden.value = fmt(sel[0]);
          delHidden.dispatchEvent(new Event('input', { bubbles: true }));
          return;
        }
        // Two dates chosen: delivery + pickup, clamped to [delivery+1, delivery+maxHire].
        var del = sel[0];
        var pick = clampPickup(del, sel[1]);
        if (pick.getTime() !== sel[1].getTime()) {
          suppress = true; fp.setDate([del, pick], false); suppress = false;
        }
        commit(del, pick, true); // >7 days fires the extended-hire surcharge modal, as intended
      },
      onClose: function (sel) {
        if (suppress) { return; }
        // Closed after choosing only a delivery date: default the pickup to the included
        // hire period (delivery + hire days) — the previous default, without blocking longer hires.
        if (sel && sel.length === 1) {
          var del = sel[0];
          var pick = defaultPickup(del);
          suppress = true; fp.setDate([del, pick], false); suppress = false;
          commit(del, pick, true);
        }
      }
    });
    if (fp.calendarContainer) { fp.calendarContainer.classList.add('lsbh-fp'); }

    // Seed a default range on load (delivery = next eligible day, pickup = +included hire),
    // matching the pre-fill of the previous two-field UI so validation passes without a
    // manual pick. No modal on seed.
    var seedDel = deliveryMinDate();
    var seedPick = defaultPickup(seedDel);
    suppress = true;
    fp.setDate([seedDel, seedPick], false);
    suppress = false;
    commit(seedDel, seedPick, false);

    // Add-to-cart guard hook, identical contract to the two-field pair.
    container.__lsbhHire = {
      isExtended: isExtended,
      isAcknowledged: function () { return true; }, // OWNER 2026-07-18: popup removed — never hold the submit
      sync: syncExtProp,
      prompt: function (cb) { syncExtProp(); if (cb) { cb(); } } // record length, no modal
    };
  }

  /* OLT-001 — preferred delivery window. The visible <select> carries no name; it drives
   * a hidden, initially-disabled mirror input so an unselected window posts nothing, and a
   * chosen one posts properties[Preferred delivery window]. Same pattern as the T&C /
   * permit hidden inputs already in the theme. */
  function initWindows() {
    var sels = document.querySelectorAll('[data-lsbh-delivery-window]');
    for (var i = 0; i < sels.length; i++) {
      (function (sel) {
        if (sel.getAttribute('data-lsbh-win-init') === '1') { return; }
        sel.setAttribute('data-lsbh-win-init', '1');
        var field = sel.closest('[data-lsbh-window-field]') || sel.parentNode;
        var prop = field ? field.querySelector('[data-lsbh-window-prop]') : null;
        function sync() {
          if (!prop) { return; }
          var v = sel.value || '';
          if (v) { prop.disabled = false; prop.value = v; }
          else { prop.disabled = true; prop.value = ''; }
        }
        sel.addEventListener('change', sync);
        sync();
      })(sels[i]);
    }
  }

  function initAll() {
    var nodes = document.querySelectorAll('[data-lsbh-dp]');
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].querySelector('[data-lsbh-dp-range]')) { initRange(nodes[i]); }
      else { initPair(nodes[i]); }
    }
    initWindows();
  }

  /* ===== Add-to-cart backstop =====
   * Nothing is ever rejected here. This exists because a range supplied by URL param,
   * back-button restore or prefill never passes through a picker's onChange, so it would
   * otherwise reach checkout without the customer ever being told about the surcharge.
   * Returns false ONLY to hold the submit open while the modal is answered; "Continue
   * booking" re-submits the form. The property is attached either way.
   */
  window.LSBH_hireGuard = function (form) {
    var scope = form || document;
    var nodes = scope.querySelectorAll('[data-lsbh-dp]');
    for (var i = 0; i < nodes.length; i++) {
      var api = nodes[i].__lsbhHire;
      if (!api) { continue; }
      api.sync(); // property attaches in all cases, acknowledged or not
      if (api.isExtended() && !api.isAcknowledged()) {
        (function (a, f) {
          a.prompt(function () {
            // Acknowledged: re-run the submit the customer already asked for.
            if (f && typeof f.requestSubmit === 'function') { f.requestSubmit(); }
            else if (f) { f.submit(); }
          });
        })(api, form);
        return false;
      }
    }
    return true;
  };
  window.LSBH_INCLUDED_HIRE_DAYS = INCLUDED_HIRE_DAYS;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  window.LSBH_initDeliveryPickup = initAll; // allow re-init after cart re-render
  // T7: expose the shared Sydney date logic + 8pm cutoff so the countdown line stays in
  // lock-step with the picker's minimum selectable date.
  window.LSBH_DELIVERY = { nextDate: deliveryMinDate, now: sydneyToday, cutoffHour: 20 };
})();
